import { computed } from 'vue';
import { useTranslation } from '@/composables/useTranslation';
import { parseDateTimeFromSAPI } from '@/utils/sapi';
import { shortDuration, decimalDuration } from '@/utils/formatting';
import { type ValueOf } from '@/types';
import { DateFormat } from '@/constants/formatting';
import { Setting } from '@/constants/settings';
import { useSettings } from '@/composables/settings/useSettings';
import {
	convertUserDateFormatToDateFns,
	convertUserTimeFormatToDateFns,
	dateToMysqlString,
} from './formatterUtils';
import { formatters } from './formatters';
import { getCurrentDateFnsLocale } from './dateFnsLocale';
import { format, parse } from 'date-fns';

/**
 * A hook for automatically formatting things to the user's specified format.
 */
export const useFormatter = () => {
	const { t } = useTranslation();
	const { setting } = useSettings();
	const currentDateFnsLocale = getCurrentDateFnsLocale();

	const userDateFormat = computed(() => setting(Setting.LOCALE_DATE));
	const userTimeFormat = computed(() => setting(Setting.LOCALE_CLOCK));
	const userLocaleNumber = computed<string>(
		() => setting(Setting.LOCALE_NUMBER) || ''
	);
	const userThousandsSeparator = computed<string>(() => {
		if (userLocaleNumber.value[0] === '|') {
			return ' ';
		}

		return userLocaleNumber.value.split('|')[0];
	});
	const userDecimalSeparator = computed<string>(() => {
		return userLocaleNumber.value.split('|')[1];
	});
	const userWorkReportHoursToInt = computed(() =>
		setting(Setting.CONVERT_BILLABLE_HOURS_TO_INT)
	);

	/**
	 * Internal utility function for creating formatters with the given date
	 * format. The created formatter takes into account the user's date & time
	 * format preferences.
	 */
	const formatDateTimeInternal =
		(dateFormat: ValueOf<typeof DateFormat>) =>
		(dateOrString: Date | string) => {
			if (!userDateFormat.value || !userTimeFormat.value) {
				if (process.env.NODE_ENV === 'test') {
					return 'DEBUG: No Settings have been fetched yet!!';
				}
				return '';
			}

			// Convert string to date object
			const date =
				typeof dateOrString === 'string'
					? parseDateTimeFromSAPI(dateOrString)
					: dateOrString;

			// The formatting logic is built on composing small formatting
			// functions. Each date format declares their formatting function in
			// the `formatters` map.
			const formattedDate = formatters[dateFormat]({
				date,
				dateFormat: convertUserDateFormatToDateFns(
					userDateFormat.value
				),
				timeFormat: convertUserTimeFormatToDateFns(
					userTimeFormat.value
				),
				t,
				dateFnsLocale: currentDateFnsLocale,
			});

			return formattedDate;
		};

	const formatNumber = (
		number: number,
		{
			/**
			 * Force exactly this precision for decimals if min or max are not
			 * provided.
			 *
			 * Example:
			 * decimalPrecision: 2
			 * 1 -> "1.00"
			 * 1.111 -> "1.11"
			 */
			decimalPrecision = 2,
			/**
			 * In case of full numbers, add the decimals.
			 *
			 * Example:
			 * false:
			 * 1 -> "1"
			 * true:
			 * 1 -> "1.00"
			 */
			forceDecimalForInteger = true,
			minimumFractionDigits,
			maximumFractionDigits,
		}: {
			decimalPrecision?: number;
			forceDecimalForInteger?: boolean;
			minimumFractionDigits?: number;
			maximumFractionDigits?: number;
		} = {}
	): string => {
		const isInt = Number.isInteger(number);
		const fractionDigits =
			!forceDecimalForInteger && isInt ? 0 : decimalPrecision;

		const formattingGroups = new Intl.NumberFormat('en-US', {
			minimumFractionDigits: minimumFractionDigits ?? fractionDigits,
			maximumFractionDigits: maximumFractionDigits ?? fractionDigits,
		}).formatToParts(number);

		return formattingGroups
			.map((group) => {
				switch (group.type) {
					case 'group':
						return {
							...group,
							value: userThousandsSeparator.value,
						};
					case 'decimal':
						return { ...group, value: userDecimalSeparator.value };
					default:
						return group;
				}
			})
			.map((group) => group.value)
			.join('');
	};

	const formatDecimal = (decimal: number) => {
		// TODO: Take into account the user's settings. I created a new project
		// for this: https://landmark.scoro.ee/projects/view/6552/
		return decimal.toFixed(2);
	};

	const formatMoney = (value: number, roundMonetaryValueTreshold = 10000) => {
		let suffix = '';
		let tmpValue = value;
		let decimalPrecision = 0;

		if (value > roundMonetaryValueTreshold) {
			tmpValue = value / 1000;
			suffix = 'K';
			decimalPrecision = 1;
		}

		return `${formatNumber(tmpValue, {
			forceDecimalForInteger: false,
			decimalPrecision,
		})}${suffix}`;
	};

	const dateFormatForSUIdatePicker = computed(() =>
		(setting(Setting.LOCALE_DATE) || '')
			.toUpperCase()
			.split('')
			.map((char: string) => {
				if (['D', 'M', 'Y'].includes(char)) {
					return char === 'Y' ? char.repeat(4) : char.repeat(2);
				}

				return ['.', '/', '-'].includes(char) ? char : '';
			})
			.join('')
	);

	const formatLongDurationAsDataPair = ({
		duration,
		removeMinutesOnFullHours = false,
		minutesUnitExtraShort = false,
		padMinutesIfNoHours = true,
	}: {
		duration: number;
		/**
		 * Don't show `00min` when set true
		 */
		removeMinutesOnFullHours?: boolean;
		/**
		 * Use the "extra short" minute unit, instead of "min".
		 */
		minutesUnitExtraShort?: boolean;
		/**
		 * Pad single-digit minutes if there are no hours, e.g., "5min" instead
		 * of "05min". Defaults to `true`.
		 */
		padMinutesIfNoHours?: boolean;
	}) => {
		const totalMinutes = Math.floor(duration / 60);

		const prefix = totalMinutes < 0 ? '-' : '';
		const absoluteMinutes = Math.abs(totalMinutes);
		const minutesNumber = absoluteMinutes % 60;
		const hasHours = absoluteMinutes / 60 >= 1;

		const minutes =
			padMinutesIfNoHours || hasHours
				? `${minutesNumber}`.padStart(2, '0')
				: minutesNumber;
		const minutesDataPair = {
			value: `${!hasHours ? prefix : ''}${minutes}`,
			unit: minutesUnitExtraShort
				? t('general.minutesExtraShort')
				: t('minutesShort'),
		};

		const result = [];

		if (!removeMinutesOnFullHours || minutesNumber > 0) {
			result.push(minutesDataPair);
		}

		if (hasHours || result.length === 0) {
			const hours = Math.floor(absoluteMinutes / 60);
			const hoursDataPair = {
				value: `${prefix}${formatNumber(hours, {
					decimalPrecision: 0,
					forceDecimalForInteger: false,
				})}`,
				unit: t('hourShort'),
			};
			result.unshift(hoursDataPair);
		}

		return result;
	};

	/**
	 * Formats the given seconds to human readable time.
	 *
	 * Examples:
	 * "40h 0min"
	 * "10min"
	 */
	const formatLongDuration = (
		args: Parameters<typeof formatLongDurationAsDataPair>[0]
	) => {
		return formatLongDurationAsDataPair(args)
			.map((d) => `${d.value}${d.unit}`)
			.join(' ');
	};

	/**
	 * Formats seconds in a short duration format
	 *
	 * Example: 8:30 h
	 * @param duration The number of seconds to format
	 */
	const formatShortDuration = (duration: number): string => {
		return shortDuration(duration)
			.map((d) => `${d.value} ${d.unit}`)
			.join(' ');
	};

	/**
	 * Formats seconds in hours in decimal format
	 *
	 * Example: 8.5 h
	 * @param duration The number of seconds to format
	 * @returns string with the decimal value, formatted according to user settings, and unit
	 */
	const formatDecimalDuration = (duration: number) => {
		const decimalDurationResult = decimalDuration(duration);
		return `${formatNumber(decimalDurationResult.value, {
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
		})} ${decimalDurationResult.unit}`;
	};

	/**
	 * Formats duration in Work Report according to user's time format settings
	 *
	 * @param duration The number of seconds to format
	 * @returns string. for example, either in 1.5 h or 1:30 h format
	 */
	const formatWorkReportDuration = (duration: number) => {
		if (parseInt(userWorkReportHoursToInt.value || '0')) {
			return formatDecimalDuration(duration);
		}
		return formatShortDuration(duration);
	};

	/**
	 * Formats time from the given date object or string
	 *
	 * @param dateOrString Date object or date string from Sapi
	 * @example formatTime(new Date()) // 09:00
	 * @returns string, for example, 10:00
	 */
	const formatTime = (dateOrString: Date | string) => {
		const date =
			typeof dateOrString === 'string'
				? parseDateTimeFromSAPI(dateOrString)
				: dateOrString;

		return `${date.getHours()}:${date
			.getMinutes()
			.toString()
			.padStart(2, '0')}`;
	};

	/**
	 * Formats hour from the given string in 24h format according to user userTimeFormat setting
	 *
	 * @param string in 24h format
	 * @example formatHour('14:25')
	 * @returns string, for example, '2:25 PM'
	 */
	const formatHour = (timeString: string): string => {
		const parsedTime = parse(timeString, 'HH:mm', new Date());
		const is24Hour = userTimeFormat.value === '24';
		return format(parsedTime, is24Hour ? 'HH:mm' : 'h:mm a');
	};

	return {
		formatLongDuration,
		formatDecimal,
		formatNumber,
		formatMoney,

		// Helper functions with already applied format
		formatDateShortNoCurrentYear: formatDateTimeInternal(
			DateFormat.DATE_SHORT_NO_CURRENT_YEAR
		),
		formatDate: formatDateTimeInternal(DateFormat.DATE),
		formatDateTimeFull: formatDateTimeInternal(DateFormat.DATE_TIME_FULL),
		formatDateWithTodayTime: formatDateTimeInternal(
			DateFormat.DATE_RELATIVE_TO_TODAY_WITH_TIME
		),
		formatDateNoCurrentYear: formatDateTimeInternal(
			DateFormat.DATE_NO_CURRENT_YEAR
		),
		formatMonthShortNoCurrentYear: formatDateTimeInternal(
			DateFormat.MONTH_SHORT_NO_CURRENT_YEAR
		),
		onlyMonthAsWord: formatDateTimeInternal(DateFormat.ONLY_MONTH_AS_WORD),
		onlyDay: formatDateTimeInternal(DateFormat.ONLY_DAY_NUMBER),
		onlyYear: formatDateTimeInternal(DateFormat.ONLY_YEAR_NUMBER),
		onlyWeekOfYear: formatDateTimeInternal(DateFormat.ONLY_WEEK_OF_YEAR),
		dateFormatForSUIdatePicker,
		formatShortDuration,
		shortDuration,
		formatDecimalDuration,
		formatWorkReportDuration,
		dateToMysqlString,
		formatLongDurationAsDataPair,
		formatTime,
		formatHour,
	};
};
